import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import classnames from 'classnames';
import notify, { MESSAGES } from '../../utils/notify';
import dayjs from 'dayjs';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { sanitizeHtml } from '../../utils/useSanitize';
import ActionButton from '../../Components/Common/ActionButton';
import { SkeletonCard } from '../../Components/Common/Skeleton';
import useAsyncAction from '../../Components/Hooks/useAsyncAction';
import {
  Col,
  Container,
  Modal,
  ModalBody,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from 'reactstrap';
import PageBreadcrumb from '../../Components/Common/PageBreadcrumb';
import { DetailHeader } from '../../Components/Common/Detail';
import RequirementTab from './components/RequirementTab';
import SupportDocumentTab from './components/SupportDocumentTab';
import DeadlineCountdown from './components/DeadlineCountdown';
import ProcurementGtcTab from '../../shared/offer-domain/procurement/ProcurementGtcTab';
import useRespondClauseDetail from '../../shared/offer-domain/respond/useRespondClauseDetail';
import useRespondTypeResponse from '../../shared/offer-domain/respond/useRespondTypeResponse';
import {
  getVisibleProcurementClauses,
  getProcurementMitraFirstItem,
  getProcurementMitraId,
  TYPE_RESPONSE_COUNTER_DELAY_PENALTY_TERM,
} from '../../shared/offer-domain/respond';
import {
  DELAY_PENALTY_UNRESOLVED_MESSAGE,
  isDelayPenaltyResolved,
  pickLatestCounter,
} from '../../shared/offer-domain/payment';
import { api } from '../../config';
import { getAuthToken } from '../../auth/tokenvalidation';
import { unwrapProcurement } from '../../shared/offer-domain/procurement';
import {
  isOfferDetailReadOnly,
  isOfferInvitationValidationBlocked,
} from './utils/offerEditAccess';
import {
  buildConfirmedClauseIds,
  getUnacceptedClauses,
  isClauseAccepted,
} from './utils/clauseConfirmation';
import {
  CARGO_ALPHA_REQUIRED_MESSAGE,
  validateCargoAlphaBeforeSubmit,
} from './utils/cargoAlphaValidation';
import { getProcurementProductTypeNames } from '../../shared/offer-domain/constants';
import { formatDateTime } from '../../shared/offer-domain/counter';
import { SectionAlert } from '../../shared/offer-domain/ui';
import { DateFormatmmmyyyy } from '../../utils/dateformatter';
import {
  mapParcelPricingDisplay,
  sortByCreatedDateAsc,
} from '../../Components/QuantityTable';
import metaMask from '../../assets/images/nft/newrecord.png';
import StatusBadge from '../../Components/Common/StatusBadge';
import './OfferDetail.css';

const BUYER_NAME = 'PT Pertamina Patra Niaga';

const getContractPeriod = form => {
  const isSpot = parseInt(form?.contractType, 10) === 2;
  if (isSpot) {
    return {
      isSpot,
      label: 'Period',
      value: DateFormatmmmyyyy(
        form.contractPeriodSpot || form.contractPeriodTermStart
      ),
    };
  }
  const start = DateFormatmmmyyyy(form.contractPeriodTermStart) || '';
  const end = form.contractPeriodTermEnd
    ? DateFormatmmmyyyy(form.contractPeriodTermEnd)
    : '';
  return {
    isSpot,
    label: 'Contract Period',
    value: end ? `${start} - ${end}` : start,
  };
};

const TABS = [
  { id: 1, label: 'Input Offering', icon: 'ri-store-2-line' },
  { id: 2, label: 'Document Attachment', icon: 'ri-file-text-line' },
  { id: 3, label: 'GTC', icon: 'ri-scales-3-line' },
];

const getOfferingStatusTone = wording => {
  if (wording === 'Opening') return 'primary';
  if (wording === 'Past Deadline') return 'warning';
  if (wording === 'Closing') return 'muted';
  return 'danger';
};

const getSubmissionStatusTone = wording => {
  if (wording === 'Not Started') return 'danger';
  if (wording === 'Drafted') return 'primary';
  return 'muted';
};

const parseProcurementDate = value => {
  if (value == null || value === '') return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.toDate() : null;
};

const OfferForm = () => {
  const token = getAuthToken();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const {
    statusOfferingWording,
    submissionStatusWording,
    isLateSubmission,
    idOffer: idOfferFromState,
  } = location.state || {};
  const [isLoadingMitra, setIsLoadingMitra] = useState(Boolean(id));
  const [isLoadingProcurement, setIsLoadingProcurement] = useState(false);
  const [isLoadingQty, setIsLoadingQty] = useState(false);
  const [isProcurementSettled, setIsProcurementSettled] = useState(false);
  const [activeTab, setactiveTab] = useState(1);
  const [visitedTabs, setVisitedTabs] = useState({ 1: true });
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});
  const [qtyDeliveryData, setQtyDeliveryData] = useState({ items: [] });
  const [formMain, setFormMain] = useState({});
  const [form, setForm] = useState({
    procurementNumber: '',
    statusProcurement: '',
    titleProcurement: '',
    description: '',
    procurementType: '',
    contractType: '',
    contractPeriodSpot: '',
    contractPeriodTermStart: '',
    contractPeriodTermEnd: '',
    productTypeId: '',
    productType: '',
    productName: '',
    productNameId: '',
    operationalTolerance: '',
    partyType: '',
    operationalToleranceCounter: null,
    operationalToleranceOptionsCounter: null,
    operationalToleranceOptionsCounterLabel: null,
    productSpecification: '',
    ratioLpgId: '',
    ratioLpg: '',
    refPropaneFrequency: null,
    refPropaneRangeQuantityFrom: null,
    refPropaneRangeQuantityTo: null,
    refButaneFrequency: null,
    refButaneRangeQuantityFrom: null,
    refButaneRangeQuantityTo: null,
    notesForQuantityAndDeliveryTerms: '',
    globalNotes: '',
    quantityDiscrepancy: '',
    delivery: '',
    nominationProcedure: '',
    firstTimeSupply: '',
    redChannelPolicy: '',
    deliveryCustom: '',
    invoicingQuantityId: '',
    invoicingQuantity: '',
    modeInvoicingQuantityId: '',
    modeInvoicingQuantity: '',
    delayPenaltyAmount: null,
    descriptionOfDelayPenaltyTerms: '',
    descriptionOfPaymentTerms: '',
    paymentCurrencyId: '',
    paymentCurrency: '',
    lawId: '',
    law: '',
    arbitrationId: '',
    arbitration: '',
    notesForLoadingPort: '',
    portRestriction: [],
    otherTermsAndCondition: '',
    additionalNotes: '',
    counterDelayPenaltyTerm: [],
    counterPaymentTerm: [],
    counterCompliance: [],
    procurementClauseDetails: [],
    proposalSubmissionDeadline: '',
    proposalValidityPeriodMax: '',
    proposalValidityPeriodMaxCounter: null,
    fileHasilOptimasiHilir: '',
    filePenetapanHargaDasarImpor: '',
    fileSpesifikasi: '',
    fileGeneralTermsAndConditions: '',
    fileDocumentQ88: '',
    fileDocumentGasFormC: '',
    fileSupporting1: '',
    fileSupporting2: '',
    fileSupporting3: '',
    fileSupporting4: '',
    fileSupporting5: '',
    subStatus: '',
    isInformationAboutDMUTConfirmed: '',
    isAcknowledgedDMUT: '',
    hpsRequestNumber: '',
    hpsRequestDate: '',
    userId: '',
    user: '',
    isProcurementRequirementCompleted: '',
    isSupportingDocumentsCompleted: '',
    isDMUTInvitationCompleted: '',
    isHPSRequestCompleted: '',
    id: '',
    createdBy: '',
    updatedBy: '',
    createdDate: '',
    updatedDate: '',
    isDeleted: '',
    deletedDate: '',
    deletedBy: '',
  });
  const visibleClauseDetailIds = useMemo(
    () =>
      getVisibleProcurementClauses({
        procurementClauseDetails: form.procurementClauseDetails,
      })
        .map(clause => clause.id)
        .filter(Boolean),
    [form.procurementClauseDetails]
  );
  const {
    clauseResponses,
    isLoading: isClauseResponsesLoading,
    isSaving: isClauseResponsesSaving,
    saveResponse: saveClauseResponse,
    confirmResponse: confirmClauseResponse,
    confirmResponses: confirmClauseResponses,
    deleteResponse: deleteClauseResponse,
    refresh: refreshClauseResponses,
  } = useRespondClauseDetail(
    token,
    formMain.id || id,
    visibleClauseDetailIds,
    formMain.ids
  );
  const [confirmedClauseIds, setConfirmedClauseIds] = useState({});
  const [clauseConfirmFocusToken, setClauseConfirmFocusToken] = useState(0);

  useEffect(() => {
    if (isClauseResponsesLoading) return;
    setConfirmedClauseIds(buildConfirmedClauseIds(clauseResponses));
  }, [clauseResponses, isClauseResponsesLoading]);

  const confirmClause = useCallback(
    async clauseId => {
      if (!clauseId) return;
      if (
        isClauseAccepted({ id: clauseId }, clauseResponses, confirmedClauseIds)
      ) {
        return;
      }
      try {
        const saved = await confirmClauseResponse(clauseId);
        if (!saved) {
          notify.error('Failed to confirm clause. Please try again.');
          return;
        }
        setConfirmedClauseIds(prev => ({ ...prev, [clauseId]: true }));
        notify.success('Clause confirmed successfully.');
      } catch {
        notify.error('Failed to confirm clause. Please try again.');
      }
    },
    [clauseResponses, confirmedClauseIds, confirmClauseResponse]
  );

  const confirmAllClauses = useCallback(
    async clauses => {
      const list = (clauses ?? []).filter(
        clause =>
          clause?.id &&
          !isClauseAccepted(clause, clauseResponses, confirmedClauseIds)
      );
      if (!list.length) return;
      try {
        const ids = list.map(clause => clause.id);
        const results = await confirmClauseResponses(ids);
        const confirmedIds = ids.filter((_, index) => Boolean(results[index]));
        if (!confirmedIds.length) {
          notify.error('Failed to confirm clauses. Please try again.');
          return;
        }
        setConfirmedClauseIds(prev => {
          const next = { ...prev };
          confirmedIds.forEach(clauseId => {
            next[clauseId] = true;
          });
          return next;
        });
        notify.success(
          confirmedIds.length === 1
            ? 'Clause confirmed successfully.'
            : `${confirmedIds.length} clauses confirmed successfully.`
        );
      } catch {
        notify.error('Failed to confirm clauses. Please try again.');
      }
    },
    [clauseResponses, confirmedClauseIds, confirmClauseResponses]
  );

  const ensureClausesConfirmed = useCallback(() => {
    const clauses = getVisibleProcurementClauses({
      procurementClauseDetails: form.procurementClauseDetails,
    });
    const pending = getUnacceptedClauses(
      clauses,
      clauseResponses,
      confirmedClauseIds
    );
    if (!pending.length) return true;
    notify.warning(
      'Clause confirmation is incomplete. Please complete confirmation for remaining clauses before submitting the offer.'
    );
    setactiveTab(1);
    setClauseConfirmFocusToken(token => token + 1);
    return false;
  }, [form.procurementClauseDetails, clauseResponses, confirmedClauseIds]);

  // Respond type (Delay Penalty confirm, thread notes) dipakai Requirement tab
  // dan validasi submit di kedua tab.
  const typeResponseState = useRespondTypeResponse(
    token,
    formMain.ids,
    formMain.id
  );
  // Dari semua own row type 22 (baris confirm terpisah dari notes thread).
  const isDelayPenaltyConfirmed =
    typeResponseState.confirmedByType[
      TYPE_RESPONSE_COUNTER_DELAY_PENALTY_TERM
    ] === true;
  const [delayPenaltyFocusToken, setDelayPenaltyFocusToken] = useState(0);

  const ensureDelayPenaltyResolved = useCallback(() => {
    if (
      isDelayPenaltyResolved({
        isConfirmed: isDelayPenaltyConfirmed,
        counter: pickLatestCounter(form.counterDelayPenaltyTerm, formMain.id),
      })
    ) {
      return true;
    }
    notify.warning(DELAY_PENALTY_UNRESOLVED_MESSAGE);
    setactiveTab(1);
    setDelayPenaltyFocusToken(value => value + 1);
    return false;
  }, [isDelayPenaltyConfirmed, form.counterDelayPenaltyTerm, formMain.id]);

  const [cargoAlphaFocusToken, setCargoAlphaFocusToken] = useState(0);

  const ensureCargoAlphaOffered = useCallback(async () => {
    try {
      const result = await validateCargoAlphaBeforeSubmit({
        procurementId: formMain.ids,
        termIds: qtyDeliveryData.items.map(item => item.id),
        token,
        form,
      });
      if (result.ok) return true;
      notify.warning(result.message || CARGO_ALPHA_REQUIRED_MESSAGE);
    } catch {
      notify.warning(CARGO_ALPHA_REQUIRED_MESSAGE);
    }
    setactiveTab(1);
    setCargoAlphaFocusToken(value => value + 1);
    return false;
  }, [formMain.ids, qtyDeliveryData.items, token, form]);

  const fetchDataInvitation = async (id, token) => {
    setIsLoadingMitra(true);
    try {
      const response = await axios.get(
        `${api.API_URL}/dmut/get-procurement-mitra/${id}`,
        {
          withXSRFToken: true,
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: `application/json`,
          },
        }
      );
      const firstItem = getProcurementMitraFirstItem(response);
      if (!firstItem) return;

      const proposalDate = firstItem.procurement?.proposalValidityPeriodMax
        ? new Date(firstItem.procurement.proposalValidityPeriodMax)
        : null;
      setFormMain({
        ids: firstItem.procurementId || null,
        id: getProcurementMitraId(firstItem) || id || null,
        idOffer:
          firstItem.idOffer ?? firstItem.IdOffer ?? idOfferFromState ?? null,
        companyName: firstItem.companyMitra?.companyName || null,
        paymentTermResponse:
          sanitizeHtml(firstItem.paymentTermResponse) || null,
        paymentCurrencyResponse: firstItem.paymentCurrencyResponse || null,
        lawArbitrationResponse: firstItem.lawArbitrationResponse || null,
        loadingPortResponse: firstItem.loadingPortResponse || null,
        addLoadingPortResponse: firstItem.addLoadingPortResponse || null,
        additionalNotesResponse:
          sanitizeHtml(firstItem.additionalNotesResponse) || null,
        statusOffering: firstItem.statusOffering ?? null,
        submissionStatus: firstItem.submissionStatus ?? null,
        statusOfferingWording: statusOfferingWording ?? null,
        statusOfferingWordingFlag: isOfferInvitationValidationBlocked({
          proposalDate,
          submissionStatus: firstItem.submissionStatus,
          statusOfferingWording: statusOfferingWording ?? null,
        }),
      });
    } catch {
      void 0;
    } finally {
      setIsLoadingMitra(false);
    }
  };

  const fetchDataInvitationValidation = async (id, token) => {
    try {
      const response = await axios.get(
        `${api.API_URL}/dmut/get-procurement-mitra/${id}`,
        {
          withXSRFToken: true,
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: `application/json`,
          },
        }
      );
      const firstItem = getProcurementMitraFirstItem(response);
      if (!firstItem) return false;

      const proposalDate = firstItem.procurement?.proposalValidityPeriodMax
        ? new Date(firstItem.procurement.proposalValidityPeriodMax)
        : null;
      return isOfferInvitationValidationBlocked({
        proposalDate,
        submissionStatus: firstItem.submissionStatus,
        statusOfferingWording: statusOfferingWording ?? null,
      });
    } catch {
      void 0;
    }
  };

  useEffect(() => {
    fetchDataInvitation(id, token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token]);

  useEffect(() => {
    if (formMain.ids) {
      fetchDataProcurement(formMain.ids, token);
    }
  }, [formMain.ids, token]);

  useEffect(() => {
    fetchQtyDeliveryList(formMain.ids, token);
  }, [formMain.ids, token]);

  useEffect(() => {
    setVisitedTabs(prev =>
      prev[activeTab] ? prev : { ...prev, [activeTab]: true }
    );
  }, [activeTab]);

  const fetchDataProcurement = async (ids, token) => {
    setIsLoadingProcurement(true);
    try {
      const response = await axios.get(`${api.API_URL}/procurement/${ids}`, {
        withXSRFToken: true,
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: `application/json`,
        },
      });
      const data = unwrapProcurement(response) ?? response;
      const normalizedProcurement = data;
      setForm({
        procurementNumber: data.procurementNumber || null,
        statusProcurement: data.statusProcurement || null,
        titleProcurement: data.titleProcurement || null,
        description: data.description || null,
        procurementType: data.procurementType || null,
        contractType: data.contractType || null,
        contractPeriodSpot: data.contractPeriodSpot || null,
        contractPeriodTermStart: data.contractPeriodTermStart || null,
        contractPeriodTermEnd: data.contractPeriodTermEnd || null,
        productTypeId: data.productTypeId || null,
        productType: data.productType || null,
        procurementProductTypes:
          data.procurementProductTypes ?? data.ProcurementProductTypes ?? [],
        productTypename: data.productType.name || null,
        productName: data.productType?.product?.name || null,
        productNameId: data.productType?.product?.id || null,
        operationalTolerance: data.operationalTolerance || null,
        partyType: data.partyType || null,
        operationalToleranceCounter:
          data.operationalToleranceCounter ??
          data.OperationalToleranceCounter ??
          null,
        operationalToleranceOptionsCounter:
          data.operationalToleranceOptionsCounter ??
          data.OperationalToleranceOptionsCounter ??
          null,
        operationalToleranceOptionsCounterLabel:
          data.operationalToleranceOptionsCounterLabel ??
          data.OperationalToleranceOptionsCounterLabel ??
          null,
        productSpecification: data.productSpecification || null,
        ratioLpgId: data.ratioLpgId || null,
        ratioLpg: data.ratioLpg || null,
        refPropaneFrequency: data.refPropaneFrequency || null,
        refPropaneRangeQuantityFrom: data.refPropaneRangeQuantityFrom || null,
        refPropaneRangeQuantityTo: data.refPropaneRangeQuantityTo || null,
        refButaneFrequency: data.refButaneFrequency || null,
        refButaneRangeQuantityFrom: data.refButaneRangeQuantityFrom || null,
        refButaneRangeQuantityTo: data.refButaneRangeQuantityTo || null,
        notesForQuantityAndDeliveryTerms:
          data.notesForQuantityAndDeliveryTerms || null,
        globalNotes: data.globalNotes || null,
        quantityDiscrepancy: data.quantityDiscrepancy || null,
        delivery: data.delivery || null,
        nominationProcedure: data.nominationProcedure || null,
        firstTimeSupply: data.firstTimeSupply || null,
        redChannelPolicy: data.redChannelPolicy || null,
        deliveryCustom: data.deliveryCustom || null,
        invoicingQuantityId: data.invoicingQuantityId || null,
        invoicingQuantity: data.invoicingQuantity || null,
        modeInvoicingQuantityId: data.modeInvoicingQuantityId || null,
        modeInvoicingQuantity: data.modeInvoicingQuantity || null,
        delayPenaltyAmount: data.delayPenaltyAmount ?? null,
        descriptionOfDelayPenaltyTerms:
          data.descriptionOfDelayPenaltyTerms || null,
        descriptionOfPaymentTerms: data.descriptionOfPaymentTerms || null,
        paymentCurrencyId: data.paymentCurrencyId || null,
        paymentCurrency: data.paymentCurrency || null,
        lawId: data.lawId || null,
        law: data.law || null,
        arbitrationId: data.arbitrationId || null,
        arbitration: data.arbitration || null,
        notesForLoadingPort: data.notesForLoadingPort || null,
        portRestriction: data.portRestriction || [],
        otherTermsAndCondition: data.otherTermsAndCondition || null,
        additionalNotes: data.additionalNotes || null,
        counterDelayPenaltyTerm: data.counterDelayPenaltyTerm || [],
        counterPaymentTerm: data.counterPaymentTerm || [],
        counterCompliance: data.counterCompliance || [],
        proposalSubmissionDeadline: parseProcurementDate(
          data.proposalSubmissionDeadline
        ),
        proposalValidityPeriodMax: parseProcurementDate(
          data.proposalValidityPeriodMax
        ),
        proposalValidityPeriodMaxCounter: parseProcurementDate(
          data.proposalValidityPeriodMaxCounter ??
            data.ProposalValidityPeriodMaxCounter
        ),
        fileHasilOptimasiHilir: data.fileHasilOptimasiHilir || null,
        filePenetapanHargaDasarImpor: data.filePenetapanHargaDasarImpor || null,
        fileSpesifikasi: data.fileSpesifikasi || null,
        fileGeneralTermsAndConditions:
          data.fileGeneralTermsAndConditions || null,
        fileDocumentQ88: data.fileDocumentQ88 || null,
        fileDocumentGasFormC: data.fileDocumentGasFormC || null,
        fileSupporting1: data.fileSupporting1 || null,
        fileSupporting2: data.fileSupporting2 || null,
        fileSupporting3: data.fileSupporting3 || null,
        fileSupporting4: data.fileSupporting4 || null,
        fileSupporting5: data.fileSupporting5 || null,
        subStatus: data.subStatus || null,
        isInformationAboutDMUTConfirmed:
          data.isInformationAboutDMUTConfirmed || null,
        isAcknowledgedDMUT: data.isAcknowledgedDMUT || null,
        hpsRequestNumber: data.hpsRequestNumber || null,
        hpsRequestDate: data.hpsRequestDate || null,
        userId: data.userId || null,
        user: data.user || null,
        isProcurementRequirementCompleted:
          data.isProcurementRequirementCompleted || null,
        isSupportingDocumentsCompleted:
          data.isSupportingDocumentsCompleted || null,
        isDMUTInvitationCompleted: data.isDMUTInvitationCompleted || null,
        isHPSRequestCompleted: data.isHPSRequestCompleted || null,
        id: data.id || null,
        createdBy: data.createdBy || null,
        updatedBy: data.updatedBy || null,
        createdDate: data.createdDate || null,
        updatedDate: data.updatedDate || null,
        isDeleted: data.isDeleted || null,
        deletedDate: data.deletedDate || null,
        deletedBy: data.deletedBy || null,
        procurementClauseDetails:
          normalizedProcurement.procurementClauseDetails ?? [],
      });
    } catch {
      void 0;
    } finally {
      setIsLoadingProcurement(false);
      setIsProcurementSettled(true);
    }
  };

  function toggleTab(tab) {
    if (activeTab !== tab && tab >= 1 && tab <= TABS.length) {
      setactiveTab(tab);
    }
  }

  const fetchQtyDeliveryList = async (ids, token) => {
    if (!ids) {
      return;
    }
    setIsLoadingQty(true);
    setError(null);

    try {
      const response = await fetch(
        `${api.API_URL}/quantity-delivery?ProcurementId=${ids}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error(
          `HTTP error: ${response.status} ${response.statusText}`
        );
      }
      const result = await response.json();

      if (!result.data?.items) {
        return;
      }
      const items = sortByCreatedDateAsc(
        result.data.items.map(item => {
          const pricing = mapParcelPricingDisplay(item);
          return {
            procurementId: item.procurementId,
            ratioLpgId: item.ratioLpgId,
            ratioName: item.ratioLpg?.ratioName ?? null,
            deliveryUnit: item.deliveryUnit,
            productTypeId: item.productTypeId ?? null,
            isDyed: item.isDyed !== false,
            isUndyed: item.isUndyed !== false,
            frequency: item.frequency,
            cargoType: item.cargoType,
            isCorrelationBetweenTwoIndex: item.isCorrelationBetweenTwoIndex,
            id: item.id,
            createdDate: item.createdDate ?? null,
            ...pricing,
            indexPricename: pricing.indexPrice,
            otherIndexPricename: pricing.otherIndexPrice,
          };
        })
      );
      setQtyDeliveryData({ items });
    } catch (error) {
      setIsLoadingQty(false);
      setError(error.message);
    } finally {
      setIsLoadingQty(false);
    }
  };

  const [modal_handleInvitationAccept, setmodal_handleInvitationAccept] =
    useState(false);
  function handleInvitationAccept() {
    setmodal_handleInvitationAccept(!modal_handleInvitationAccept);
  }

  const selectedFieldsOffer = {
    id: formMain.id,
    paymentTermResponse: formMain.paymentTermResponse,
    additionalNotesResponse: formMain.additionalNotesResponse,
  };

  const [handleSaveAsDraft] = useAsyncAction(async () => {
    try {
      //setIsLoading(true);
      await axios.post(
        `${api.API_URL}/dmut/respond-offer?id=${id}&status=1`,
        selectedFieldsOffer,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      notify.success(MESSAGES.updated);
    } catch (err) {
      notify.apiError(err, 'save the offer');
    } finally {
      //setIsLoading(false);
    }
  });

  const [handleSaveAsOffer, isSavingOffer] = useAsyncAction(async () => {
    try {
      await axios.post(
        `${api.API_URL}/dmut/respond-offer?id=${id}&status=2`,
        selectedFieldsOffer,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      notify.success(MESSAGES.saved);
      navigate(`/apps-offer/view`);
    } catch (err) {
      notify.apiError(err, 'save the offer');
    }
  });

  const isHeaderLoading =
    !isProcurementSettled && (isLoadingMitra || Boolean(formMain.ids));

  const statusOfferingLabel = statusOfferingWording;
  const submissionStatusLabel = submissionStatusWording;
  const offerNumber = formMain.idOffer || idOfferFromState;
  const contractPeriod = getContractPeriod(form);
  const productLabel = [
    form.productName,
    getProcurementProductTypeNames(form) || form.productTypename,
  ]
    .filter(Boolean)
    .join(' · ');
  const isReadOnly =
    !isLoadingMitra &&
    isOfferDetailReadOnly({
      statusOfferingWordingFlag: formMain.statusOfferingWordingFlag,
      statusOfferingWording: formMain.statusOfferingWording,
      submissionStatus: formMain.submissionStatus,
    });

  document.title = 'Offer Detail';

  return (
    <React.Fragment>
      <div className='page-content od-page'>
        <Container fluid>
          <PageBreadcrumb
            fallback={{ label: 'Offer', to: '/apps-offer/view' }}
            current={`Detail #${offerNumber || '-'}`}
            listClassName='p-3 py-2 bg-light'
          />

          <div className='od-hero'>
            <DetailHeader
              onBack={() => navigate('/apps-offer/view')}
              backLabel='Back to offers'
              loading={isHeaderLoading}
              eyebrow={
                <>
                  <span className='od-chip'>
                    <i className='ri-hashtag' aria-hidden='true' />
                    {form.procurementNumber || '-'}
                  </span>
                  {offerNumber && (
                    <span className='od-chip'>
                      <i className='ri-price-tag-3-line' aria-hidden='true' />
                      Offer {offerNumber}
                    </span>
                  )}
                  {form.contractType && (
                    <span className='od-chip'>
                      {contractPeriod.isSpot ? 'Spot' : 'Term'}
                    </span>
                  )}
                </>
              }
              title={form.titleProcurement}
              badges={
                <>
                  {statusOfferingLabel && (
                    <StatusBadge
                      tone={getOfferingStatusTone(statusOfferingLabel)}
                      label={`Offering: ${statusOfferingLabel}`}
                    />
                  )}
                  {submissionStatusLabel && (
                    <StatusBadge
                      tone={getSubmissionStatusTone(submissionStatusLabel)}
                      label={`Submission: ${submissionStatusLabel}`}
                    />
                  )}
                  {isLateSubmission === true && (
                    <StatusBadge tone='danger' label='Late Submit' />
                  )}
                  {isReadOnly && <StatusBadge tone='muted' label='View only' />}
                  {(form.updatedDate || form.createdDate) && (
                    <span className='text-muted small d-inline-flex align-items-center gap-1'>
                      <i className='ri-time-line' aria-hidden='true' />
                      Updated{' '}
                      {dayjs(form.updatedDate || form.createdDate).format(
                        'DD MMM YYYY'
                      )}
                    </span>
                  )}
                </>
              }
              actions={
                <DeadlineCountdown deadline={form.proposalSubmissionDeadline} />
              }
              alert={
                <SectionAlert
                  tone='info'
                  icon='ri-shield-user-line'
                  title='Your company identity is masked'
                  className='mb-0'
                  titleClassName='fs-13'
                >
                  <div className='text-muted small'>
                    Your company name stays confidential until the tender
                    evaluation is completed, so every offer is assessed fairly.
                  </div>
                </SectionAlert>
              }
              facts={[
                {
                  icon: 'ri-building-4-line',
                  label: 'Buyer',
                  value: BUYER_NAME,
                },
                {
                  icon: 'ri-store-2-line',
                  label: 'Seller',
                  value: formMain.companyName,
                },
                {
                  icon: 'ri-drop-line',
                  label: 'Product',
                  value: productLabel,
                },
                {
                  icon: 'ri-calendar-2-line',
                  label: contractPeriod.label,
                  value: contractPeriod.value,
                },
                {
                  icon: 'ri-shield-check-line',
                  label: 'Offer valid to',
                  value: formatDateTime(form.proposalValidityPeriodMax),
                },
              ]}
            />
          </div>

          <div className='dt-tabs'>
            <Nav tabs className='nav-tabs-custom nav-primary'>
              {TABS.map(tab => (
                <NavItem key={tab.id}>
                  <NavLink
                    tag='button'
                    type='button'
                    className={classnames('cursor-pointer', {
                      active: activeTab === tab.id,
                    })}
                    onClick={() => toggleTab(tab.id)}
                  >
                    <i className={`${tab.icon} me-2 align-middle`}></i>
                    <span className='fw-semibold'>{tab.label}</span>
                  </NavLink>
                </NavItem>
              ))}
            </Nav>

            <TabContent activeTab={activeTab} className='p-3 p-md-4'>
              {visitedTabs[1] && (
                <TabPane tabId={1} id='pills-bill-info'>
                  {isHeaderLoading ? (
                    <Row className='g-4'>
                      <Col xl={9}>
                        <SkeletonCard lines={5} className='border mb-4' />
                        <SkeletonCard lines={6} className='border mb-4' />
                        <SkeletonCard lines={4} className='border' />
                      </Col>
                      <Col xl={3} className='d-none d-xl-block'>
                        <SkeletonCard lines={6} className='border' />
                      </Col>
                    </Row>
                  ) : (
                    <RequirementTab
                      goNext={() => setactiveTab(2)}
                      ids={formMain.ids}
                      form={form}
                      setForm={setForm}
                      formMain={formMain}
                      setFormMain={setFormMain}
                      errors={errors}
                      error={error}
                      setErrors={setErrors}
                      isLoading={
                        isLoadingMitra ||
                        isLoadingProcurement ||
                        isLoadingQty ||
                        !formMain.ids
                      }
                      qtyDeliveryData={qtyDeliveryData}
                      token={token}
                      handleInvitationAccept={handleInvitationAccept}
                      handleSaveAsDraft={handleSaveAsDraft}
                      fetchDataInvitationValidation={() =>
                        fetchDataInvitationValidation(formMain.id, token)
                      }
                      clauseResponses={clauseResponses}
                      isClauseResponsesLoading={isClauseResponsesLoading}
                      isClauseResponsesSaving={isClauseResponsesSaving}
                      saveClauseResponse={saveClauseResponse}
                      deleteClauseResponse={deleteClauseResponse}
                      refreshClauseResponses={refreshClauseResponses}
                      refreshProcurement={() =>
                        fetchDataProcurement(formMain.ids, token)
                      }
                      confirmedClauseIds={confirmedClauseIds}
                      confirmClause={confirmClause}
                      confirmAllClauses={confirmAllClauses}
                      clauseConfirmFocusToken={clauseConfirmFocusToken}
                      ensureClausesConfirmed={ensureClausesConfirmed}
                      typeResponseState={typeResponseState}
                      isDelayPenaltyConfirmed={isDelayPenaltyConfirmed}
                      delayPenaltyFocusToken={delayPenaltyFocusToken}
                      ensureDelayPenaltyResolved={ensureDelayPenaltyResolved}
                      ensureCargoAlphaOffered={ensureCargoAlphaOffered}
                      cargoAlphaFocusToken={cargoAlphaFocusToken}
                    />
                  )}
                </TabPane>
              )}
              {visitedTabs[2] && (
                <TabPane tabId={2}>
                  <SupportDocumentTab
                    ids={formMain.ids}
                    token={token}
                    formMain={formMain}
                    handleInvitationAccept={handleInvitationAccept}
                    handleSaveAsDraft={handleSaveAsDraft}
                    fetchDataInvitationValidation={() =>
                      fetchDataInvitationValidation(formMain.id, token)
                    }
                    ensureClausesConfirmed={ensureClausesConfirmed}
                    ensureDelayPenaltyResolved={ensureDelayPenaltyResolved}
                    ensureCargoAlphaOffered={ensureCargoAlphaOffered}
                  />
                </TabPane>
              )}
              {visitedTabs[3] && (
                <TabPane tabId={3}>
                  {/* Read-only; amendments are edited in Master Amend GTC. */}
                  <ProcurementGtcTab procurementId={formMain.ids} showAmend />
                </TabPane>
              )}
            </TabContent>
          </div>
        </Container>
      </div>

      <Modal
        isOpen={modal_handleInvitationAccept}
        toggle={handleInvitationAccept}
        id='submit-offer-modal'
        centered
      >
        <ModalHeader toggle={() => setmodal_handleInvitationAccept(false)} />
        <ModalBody className='text-center p-4 p-sm-5'>
          <div className='mt-1 pt-2'>
            <Row className='justify-content-center mb-1'>
              <Col lg={12} className='text-center'>
                <img src={metaMask} alt='' height='65' className='mb-2 pb-2' />
                <h5>Submit this offering?</h5>
                <p className='text-muted pb-1'>
                  Once submitted, your offering is sent to the buyer and can no
                  longer be edited or withdrawn. Make sure all input is final
                  before you continue.
                </p>
              </Col>
            </Row>
            <ActionButton
              variant='secondary'
              icon='ri-close-line'
              className='ms-2'
              disabled={isSavingOffer}
              onClick={() => setmodal_handleInvitationAccept(false)}
            >
              Cancel
            </ActionButton>
            <ActionButton
              variant='primary'
              icon='ri-database-2-line'
              className='ms-2'
              loading={isSavingOffer}
              onClick={async () => {
                await handleSaveAsOffer();
                setmodal_handleInvitationAccept(false);
              }}
            >
              Submit offering
            </ActionButton>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default OfferForm;
